import { useState, useCallback, useRef, useEffect } from 'react'
import { router } from '@inertiajs/react'
import type { AndrzejMessage, AvatarState, Conversation, CableMessage } from '@/types/andrzej'

function getStored(key: string): string | null { return localStorage.getItem(key) }
function setStored(key: string, val: string) { localStorage.setItem(key, val) }
function removeStored(key: string) { localStorage.removeItem(key) }

// Extended message type that includes synthetic tool indicator messages
interface DisplayMessage extends AndrzejMessage {
  _optimistic?: boolean
  _synthetic?: boolean  // runtime tool indicator
  _toolStatus?: 'loading' | 'completed'
  _toolIcon?: string
  _toolLabel?: string
}

export function useAndrzej() {
  const [isOpen, setIsOpen] = useState(() => getStored('andrzej-open') === 'true')
  const [conversationId, setConversationIdRaw] = useState<number | null>(() => {
    const s = getStored('andrzej-conversation-id')
    return s ? parseInt(s, 10) : null
  })
  const [messages, setMessages] = useState<DisplayMessage[]>([])
  const [avatarState, setAvatarStateRaw] = useState<AvatarState>('default')
  const [isThinking, setIsThinking] = useState(false)
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [isConvosPanelOpen, setConvosPanelOpen] = useState(false)
  const [photoFiles, setPhotoFiles] = useState<File[]>([])
  const [photoPreviews, setPhotoPreviews] = useState<string[]>([])
  const [welcomeMessage, setWelcomeMessage] = useState('Cześć! Jak mogę pomóc?')
  const [showWelcome, setShowWelcome] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)

  const avatarTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const thinkingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  const messagesEndRef = useRef<HTMLDivElement | null>(null)
  const toolCounterRef = useRef(0)

  // --- Persistence helpers ---
  const setConversationId = useCallback((id: number | null) => {
    setConversationIdRaw(id)
    if (id) setStored('andrzej-conversation-id', id.toString())
    else removeStored('andrzej-conversation-id')
  }, [])

  const toggle = useCallback(() => {
    setIsOpen(prev => {
      const next = !prev
      setStored('andrzej-open', next.toString())
      return next
    })
  }, [])

  const open = useCallback(() => { setIsOpen(true); setStored('andrzej-open', 'true') }, [])
  const close = useCallback(() => { setIsOpen(false); setStored('andrzej-open', 'false') }, [])

  // --- Avatar with auto-reset ---
  const setAvatarState = useCallback((state: AvatarState) => {
    setAvatarStateRaw(state)
    if (avatarTimeoutRef.current) clearTimeout(avatarTimeoutRef.current)
    if (state === 'happy' || state === 'writing' || state === 'concerned') {
      avatarTimeoutRef.current = setTimeout(() => setAvatarStateRaw('default'), 3000)
    }
  }, [])

  // --- Thinking with safety timeout ---
  const startThinking = useCallback(() => {
    setIsThinking(true)
    setAvatarState('thinking')
    if (thinkingTimeoutRef.current) clearTimeout(thinkingTimeoutRef.current)
    thinkingTimeoutRef.current = setTimeout(() => {
      setIsThinking(false)
      setAvatarStateRaw('default')
    }, 30000)
  }, [setAvatarState])

  const stopThinking = useCallback(() => {
    setIsThinking(false)
    if (thinkingTimeoutRef.current) clearTimeout(thinkingTimeoutRef.current)
  }, [])

  // --- Welcome message based on context ---
  const getWelcome = useCallback((contextType: string): string => {
    const map: Record<string, string> = {
      plant_form: 'Cześć! Wrzuć mi zdjęcie rośliny albo ją opisz — pomogę wypełnić formularz.',
      plant_show: 'Cześć! Pytaj o pielęgnację albo powiedz że podlałeś.',
      general: 'Cześć! Jak mogę pomóc?',
    }
    return map[contextType] || map.general
  }, [])

  // --- Load conversation from server ---
  const loadConversation = useCallback(async (id: number) => {
    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller

    try {
      const res = await fetch(`/andrzej/conversations/${id}`, { signal: controller.signal })
      if (!res.ok) throw new Error('Not found')
      const data = await res.json()
      if (controller.signal.aborted) return
      setConversationId(data.conversation_id)
      setMessages(data.messages || [])
      setShowWelcome(false)
      stopThinking()
    } catch (e) {
      if ((e as Error).name === 'AbortError') return
      resetToWelcome()
    }
  }, [setConversationId, stopThinking])

  const resetToWelcome = useCallback(() => {
    setConversationId(null)
    setMessages([])
    stopThinking()
    setShowWelcome(true)
    setWelcomeMessage(getWelcome('general'))
    toolCounterRef.current = 0
  }, [setConversationId, stopThinking, getWelcome])

  // --- Conversation list ---
  const loadConversations = useCallback(async () => {
    try {
      const res = await fetch('/andrzej/conversations')
      setConversations(await res.json())
    } catch { /* silent */ }
  }, [])

  const toggleConversationsPanel = useCallback(async () => {
    if (isConvosPanelOpen) { setConvosPanelOpen(false); return }
    await loadConversations()
    setConvosPanelOpen(true)
  }, [isConvosPanelOpen, loadConversations])

  const selectConversation = useCallback((id: number) => {
    loadConversation(id)
    setConvosPanelOpen(false)
  }, [loadConversation])

  const newConversation = useCallback(() => {
    resetToWelcome()
    setConvosPanelOpen(false)
  }, [resetToWelcome])

  // --- Send message ---
  const sendMessage = useCallback(async (content: string) => {
    const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
    const contextType = getPageContextType()
    const contextId = getPageContextId()

    const body = new FormData()
    body.append('context_type', contextType)
    body.append('context_id', contextId)
    body.append('message', content)
    if (conversationId) body.append('conversation_id', conversationId.toString())
    photoFiles.forEach(f => body.append('photos[]', f))

    // Optimistic user message
    const optimisticId = -Date.now()
    const optimistic: DisplayMessage = {
      id: optimisticId, role: 'user', content,
      created_at: new Date().toISOString(),
      attachments: photoPreviews.map((url, i) => ({ url, filename: `photo-${i + 1}` })),
      _optimistic: true,
    }
    setMessages(prev => [...prev, optimistic])
    setShowWelcome(false)
    startThinking()
    setPhotoFiles([])
    setPhotoPreviews([])
    toolCounterRef.current = 0

    try {
      const res = await fetch('/andrzej/messages', {
        method: 'POST',
        headers: { 'X-CSRF-Token': csrf || '' },
        body,
      })
      const data = await res.json()

      if (data.conversation_id) setConversationId(data.conversation_id)

      // Replace optimistic message with server version
      if (data.message) {
        setMessages(prev => prev.map(m =>
          m.id === optimisticId ? { ...data.message } : m
        ))
      }
    } catch {
      stopThinking()
      setAvatarState('concerned')
    }
  }, [conversationId, photoFiles, photoPreviews, setConversationId, startThinking, stopThinking, setAvatarState])

  // --- ActionCable handler ---
  const handleCableMessage = useCallback((data: CableMessage) => {
    switch (data.type) {
      case 'message':
        if (data.message) {
          setMessages(prev => {
            // Remove any optimistic/synthetic messages that this replaces, and deduplicate
            const withoutDupes = prev.filter(m => m.id !== data.message!.id)
            return [...withoutDupes, data.message!]
          })
          stopThinking()
          setAvatarState('writing')
        }
        break

      case 'tool_start':
        if (data.id !== undefined) {
          const syntheticId = -(1000000 + data.id)
          const synthetic: DisplayMessage = {
            id: syntheticId, role: 'tool' as const, content: null,
            created_at: new Date().toISOString(), attachments: [],
            _synthetic: true, _toolStatus: 'loading',
            _toolIcon: data.icon || '🔍', _toolLabel: data.label || '',
          }
          setMessages(prev => [...prev, synthetic])
        }
        break

      case 'tool_complete':
        if (data.id !== undefined) {
          const syntheticId = -(1000000 + data.id)
          setMessages(prev => prev.map(m =>
            m.id === syntheticId
              ? { ...m, _toolStatus: 'completed' as const, _toolLabel: data.label || m._toolLabel }
              : m
          ))
          setAvatarState('happy')
        }
        break

      case 'tool_action':
        handleToolAction(data.action || '', data.data || {})
        break

      case 'remove_thinking':
        stopThinking()
        // Remove synthetic tool indicators — they'll be replaced by real messages on next load
        // Actually keep them — they show what happened during this session
        break
    }
  }, [stopThinking, setAvatarState])

  // --- Tool actions ---
  const handleToolAction = useCallback((action: string, data: Record<string, unknown>) => {
    switch (action) {
      case 'fill_plant_form': {
        const isOnForm = window.location.pathname.includes('/plants/new') || /\/plants\/\d+\/edit/.test(window.location.pathname)
        if (isOnForm) {
          window.dispatchEvent(new CustomEvent('andrzej:fill-form', { detail: data }))
        } else {
          sessionStorage.setItem('andrzej-fill-form', JSON.stringify(data))
          router.visit('/plants/new')
        }
        if (window.innerWidth < 768) close()
        break
      }
      case 'navigate':
        if (data.url) router.visit(data.url as string)
        break
      case 'navigate_and_fill':
        if (data.form_data) {
          sessionStorage.setItem('andrzej-fill-form', JSON.stringify(data.form_data))
        }
        if (window.innerWidth < 768) close()
        router.visit((data.url as string) || '/plants/new')
        break
      case 'refresh_page':
        router.reload()
        break
    }
  }, [close])

  // --- Photos (max 5) ---
  const addPhoto = useCallback((file: File) => {
    setPhotoFiles(prev => {
      if (prev.length >= 5) return prev
      const next = [...prev, file]
      const reader = new FileReader()
      reader.onload = (e) => setPhotoPreviews(pp => [...pp, e.target?.result as string])
      reader.readAsDataURL(file)
      return next
    })
  }, [])

  const removePhoto = useCallback((index: number) => {
    setPhotoFiles(prev => prev.filter((_, i) => i !== index))
    setPhotoPreviews(prev => prev.filter((_, i) => i !== index))
  }, [])

  // --- Context helpers (read from current URL, not DOM) ---
  function getPageContextType(): string {
    const path = window.location.pathname
    if (path.match(/\/plants\/new/) || path.match(/\/plants\/\d+\/edit/)) return 'plant_form'
    const showMatch = path.match(/\/plants\/(\d+)$/)
    if (showMatch) return 'plant_show'
    return 'general'
  }

  function getPageContextId(): string {
    const match = window.location.pathname.match(/\/plants\/(\d+)/)
    return match ? match[1] : ''
  }

  // --- Read auth from page props ---
  const getAuthUserId = useCallback((pageProps?: Record<string, unknown>): number | undefined => {
    if (pageProps) {
      return (pageProps as { auth?: { user?: { id?: number } } })?.auth?.user?.id
    }
    // Fallback: read from DOM (only reliable on initial page load)
    const pageJson = document.getElementById('app')?.dataset?.page
    if (!pageJson) return undefined
    return JSON.parse(pageJson)?.props?.auth?.user?.id
  }, [])

  // --- Detect user change (chat persists outside Inertia) ---
  const checkUserChange = useCallback((pageProps?: Record<string, unknown>): boolean => {
    try {
      const userId = getAuthUserId(pageProps)
      if (!userId) {
        setIsAuthenticated(false)
        return false
      }

      setIsAuthenticated(true)
      const storedUserId = getStored('andrzej-user-id')
      if (storedUserId && storedUserId !== userId.toString()) {
        removeStored('andrzej-conversation-id')
        setConversationIdRaw(null)
        setMessages([])
        setShowWelcome(true)
        setWelcomeMessage(getWelcome('general'))
        toolCounterRef.current = 0
        stopThinking()
      }
      setStored('andrzej-user-id', userId.toString())
      return true
    } catch { return false }
  }, [getAuthUserId, stopThinking, getWelcome])

  // --- Init ---
  useEffect(() => {
    const authenticated = checkUserChange()

    if (authenticated) {
      const storedConvId = getStored('andrzej-conversation-id')
      const initConvId = storedConvId ? parseInt(storedConvId, 10) : null
      if (initConvId) loadConversation(initConvId)
      else resetToWelcome()
    }

    const removeNavigateListener = router.on('navigate', (event) => {
      const page = (event as unknown as { detail: { page: { props: Record<string, unknown> } } }).detail.page
      checkUserChange(page.props)
    })

    return () => {
      removeNavigateListener()
      abortRef.current?.abort()
      if (avatarTimeoutRef.current) clearTimeout(avatarTimeoutRef.current)
      if (thinkingTimeoutRef.current) clearTimeout(thinkingTimeoutRef.current)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // --- Auto-scroll ---
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, isThinking])

  return {
    isOpen, isAuthenticated, conversationId, messages: messages as DisplayMessage[], avatarState,
    isThinking, conversations, isConvosPanelOpen, photoPreviews,
    welcomeMessage, showWelcome, messagesEndRef,
    toggle, open, close, sendMessage, loadConversation, resetToWelcome,
    newConversation, selectConversation, toggleConversationsPanel,
    addPhoto, removePhoto, setAvatarState, handleCableMessage,
  }
}

export type { DisplayMessage }
